import React, { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight, Play, Sparkles, Shield, Zap, TrendingUp } from "lucide-react";
import ScheduleModal from "../Modal/ScheduleModal";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

const Hero = () => {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const swiperSettings = {
    modules: [Navigation, Pagination, Autoplay, EffectFade],
    loop: true,
    speed: 1000,
    slidesPerView: 1,
    spaceBetween: 0,
    effect: "fade",
    fadeEffect: {
      crossFade: true,
    },
    autoplay: {
      delay: 6000,
      disableOnInteraction: false,
    },
    pagination: {
      clickable: true,
      dynamicBullets: true,
    },
    navigation: {
      prevEl: prevRef.current,
      nextEl: nextRef.current,
    },
    onInit: (swiper) => {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;
      swiper.navigation.init();
      swiper.navigation.update();
    },
  };

  const slides = [
    {
      title: "Empowering Businesses with",
      titleHighlight: "Innovative IT Solutions",
      subtitle: "Transform Your Digital Infrastructure",
      description:
        "We deliver cutting-edge technology solutions including custom software development, cloud services, cybersecurity, and digital transformation services tailored to your business needs.",
      button1: { text: "Get Started", action: "modal" },
      button2: { text: "Explore Services", url: "/#services" },
      image: "assets/img/hero (1).jpg",
      gradient: "from-blue-600/30 via-purple-600/20 to-pink-600/30",
      icon: Sparkles,
      stats: [
        { value: "500+", label: "Projects Delivered" },
        { value: "200+", label: "Happy Clients" },
        { value: "10+", label: "Years Experience" },
      ],
    },
    {
      title: "All-in-One School",
      titleHighlight: "Management Platform",
      subtitle: "Streamlined Operations for Educational Institutions",
      description:
        "EduAdminHub centralizes core academic and administrative tasks — including student records, attendance, class schedules, and staff management — into one user-friendly platform.",
      button1: { text: "Schedule Demo", action: "modal" },
      button2: { text: "View Product", url: "/eduAdminHub" },
      image: "assets/img/hero (2).jpg",
      gradient: "from-green-600/30 via-teal-600/20 to-cyan-600/30",
      icon: Shield,
      stats: [
        { value: "50+", label: "Schools Using" },
        { value: "100K+", label: "Students Managed" },
        { value: "99.9%", label: "Uptime" },
      ],
    },
    {
      title: "Enterprise-Grade",
      titleHighlight: "Technology Services",
      subtitle: "Your Trusted IT Partner",
      description:
        "From web development to cloud infrastructure, we provide comprehensive IT solutions that drive growth, enhance efficiency, and secure your digital assets.",
      button1: { text: "Contact Us", action: "contact" },
      button2: { text: "Our Projects", url: "/project" },
      image: "assets/img/hero (3).jpg",
      gradient: "from-orange-600/30 via-red-600/20 to-pink-600/30",
      icon: Zap,
      stats: [
        { value: "24/7", label: "Support Available" },
        { value: "50+", label: "Expert Team" },
        { value: "100%", label: "Client Satisfaction" },
      ],
    },
  ];

  return (
    <>
      <section className="relative w-full min-h-[calc(100vh-4rem)] flex items-center overflow-hidden bg-gradient-to-br from-background via-muted/20 to-background pt-8 md:pt-12">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--foreground)) 1px, transparent 0)`,
            backgroundSize: '50px 50px'
          }} />
        </div>
        
        {/* Animated Gradient Orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse delay-1000" />

        {/* Navigation Buttons */}
        <button
          ref={prevRef}
          className="absolute left-2 md:left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 bg-background/95 backdrop-blur-md border border-border/50 rounded-full p-2.5 md:p-3 shadow-xl hover:bg-primary hover:text-primary-foreground hover:scale-110 hover:shadow-2xl transition-all group"
          aria-label="Previous slide"
        >
          <ChevronLeft className="h-4 w-4 md:h-5 md:w-5 lg:h-6 lg:w-6 transition-colors" />
        </button>

        <button
          ref={nextRef}
          className="absolute right-2 md:right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 bg-background/95 backdrop-blur-md border border-border/50 rounded-full p-2.5 md:p-3 shadow-xl hover:bg-primary hover:text-primary-foreground hover:scale-110 hover:shadow-2xl transition-all group"
          aria-label="Next slide"
        >
          <ChevronRight className="h-4 w-4 md:h-5 md:w-5 lg:h-6 lg:w-6 transition-colors" />
        </button>

        <Swiper {...swiperSettings} className="w-full h-full">
          {slides.map((slide, index) => {
            const Icon = slide.icon;
            return (
              <SwiperSlide key={index} className="relative">
                <div className="container">
                  <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                    {/* Content */}
                    <div className="space-y-5 md:space-y-6 text-center lg:text-left relative z-10">
                      {/* Badge */}
                      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm shadow-sm animate-in fade-in slide-in-from-top-4">
                        <Icon className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium text-primary">{slide.subtitle}</span>
                      </div>

                      {/* Title */}
                      <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] tracking-tight">
                        <span className="text-foreground block mb-2">{slide.title}</span>
                        <span className="text-primary bg-gradient-to-r from-primary via-primary/90 to-primary/70 bg-clip-text text-transparent block">
                          {slide.titleHighlight}
                        </span>
                      </h1>

                      {/* Description */}
                      <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto lg:mx-0 leading-relaxed font-light">
                        {slide.description}
                      </p>

                      {/* CTA Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start pt-2">
                        <Button
                          size="lg"
                          onClick={() => {
                            if (slide.button1.action === "modal") {
                              setIsModalOpen(true);
                            } else if (slide.button1.action === "contact") {
                              window.location.href = "/contactUs";
                            }
                          }}
                          className="group shadow-md hover:shadow-lg transition-all"
                        >
                          {slide.button1.text}
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                        <Button
                          size="lg"
                          variant="outline"
                          asChild
                          className="group hover:bg-accent transition-all"
                        >
                          <Link to={slide.button2.url}>
                            <Play className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform" />
                            {slide.button2.text}
                          </Link>
                        </Button>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-4 md:gap-6 lg:gap-8 pt-6 md:pt-8 border-t border-border/50">
                        {slide.stats.map((stat, idx) => (
                          <div key={idx} className="text-center lg:text-left group">
                            <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-1 group-hover:scale-110 transition-transform inline-block">
                              {stat.value}
                            </div>
                            <div className="text-xs md:text-sm text-muted-foreground font-medium">
                              {stat.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Image */}
                    <div className="relative order-first lg:order-last">
                      {/* Gradient Overlay */}
                      <div
                        className={cn(
                          "absolute -inset-4 bg-gradient-to-br rounded-3xl blur-3xl opacity-30 -z-10 animate-pulse",
                          slide.gradient
                        )}
                      />
                      
                      {/* Image Container */}
                      <div className="relative rounded-2xl md:rounded-3xl overflow-hidden border border-border/50 shadow-2xl group hover:shadow-3xl transition-shadow duration-500">
                        <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-background/20 to-transparent z-10" />
                        <img
                          src={slide.image}
                          alt={slide.title}
                          className="w-full h-[350px] md:h-[450px] lg:h-[550px] object-cover group-hover:scale-110 transition-transform duration-700"
                          loading="eager"
                        />
                        
                        {/* Floating Badge */}
                        <div className="absolute top-4 right-4 md:top-6 md:right-6 z-20 bg-background/95 backdrop-blur-md border border-border/50 rounded-full px-3 md:px-4 py-1.5 md:py-2 shadow-lg hover:scale-105 transition-transform">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                            <span className="text-xs md:text-sm font-medium">Live Now</span>
                          </div>
                        </div>
                      </div>

                      {/* Decorative Elements */}
                      <div className="absolute -bottom-4 -left-4 w-24 h-24 md:w-32 md:h-32 bg-primary/10 rounded-full blur-2xl -z-10 animate-pulse" />
                      <div className="absolute -top-4 -right-4 w-32 h-32 md:w-40 md:h-40 bg-primary/5 rounded-full blur-3xl -z-10 animate-pulse delay-1000" />
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        {/* Custom Pagination Styles */}
        <style jsx>{`
          :global(.swiper-pagination) {
            bottom: 2rem !important;
          }
          :global(.swiper-pagination-bullet) {
            background: hsl(var(--primary));
            opacity: 0.4;
            width: 12px;
            height: 12px;
            transition: all 0.3s;
          }
          :global(.swiper-pagination-bullet-active) {
            opacity: 1;
            width: 32px;
            border-radius: 6px;
            background: hsl(var(--primary));
          }
        `}</style>
      </section>

      <ScheduleModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default Hero;
